import React, { useState, useEffect, useRef } from 'react';
import { Search, User, Plus, X } from 'lucide-react';
import { searchCustomers } from '../../api/sales';
import { toast } from 'react-toastify';

const CustomerAutocomplete = ({ onCustomerChange, selectedCustomer }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const dropdownRef = useRef(null);
  const isMountedRef = useRef(true);

  // New customer form state
  const [newCustomer, setNewCustomer] = useState({
    full_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: ''
  });

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Search customers
  useEffect(() => {
    const searchCustomerList = async () => {
      if (searchTerm.length < 2) {
        if (isMountedRef.current) {
          setCustomers([]);
        }
        return;
      }

      if (isMountedRef.current) {
        setLoading(true);
      }

      try {
        const response = await searchCustomers(searchTerm);
        if (isMountedRef.current) {
          setCustomers(response.customers || []);
          setShowDropdown(true);
        }
      } catch (error) {
        if (isMountedRef.current) {
          toast.error('Failed to search customers');
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    const debounce = setTimeout(searchCustomerList, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectCustomer = (customer) => {
    onCustomerChange({
      customer_id: customer.id,
      customer_code: customer.customer_code,
      full_name: customer.full_name,
      phone: customer.phone,
      email: customer.email || '',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || ''
    });
    setSearchTerm('');
    setShowDropdown(false);
    toast.success(`Selected customer: ${customer.full_name}`);
  };

  const handleNewCustomerChange = (field, value) => {
    setNewCustomer(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const createNewCustomer = () => {
    // Validate required fields
    if (!newCustomer.full_name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (!newCustomer.phone.trim()) {
      toast.error('Customer phone is required');
      return;
    }

    // Pass new customer data to parent (will be created when sale is saved)
    onCustomerChange({
      customer_id: null, // Will be created
      customer_code: null,
      full_name: newCustomer.full_name.trim(),
      phone: newCustomer.phone.trim(),
      email: newCustomer.email.trim(),
      address: newCustomer.address.trim(),
      city: newCustomer.city.trim(),
      state: newCustomer.state.trim()
    });

    // Reset form
    setNewCustomer({
      full_name: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      state: ''
    });
    setShowNewCustomerForm(false);
    toast.success('New customer details added');
  };

  const clearCustomer = () => {
    onCustomerChange(null);
    setSearchTerm('');
    toast.info('Customer cleared');
  };

  return (
    <div className="space-y-4">
      {/* Selected Customer Display */}
      {selectedCustomer && (
        <div className="border border-secondary-lightGray rounded-lg p-4 bg-secondary-offWhite">
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-accent-gold" />
              <h4 className="font-semibold text-primary-black">
                {selectedCustomer.full_name}
              </h4>
            </div>
            <button
              type="button"
              onClick={clearCustomer}
              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors flex items-center justify-center"
              title="Clear customer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-secondary-gray">
            {selectedCustomer.customer_code && (
              <p>Code: <span className="font-medium">{selectedCustomer.customer_code}</span></p>
            )}
            <p>Phone: <span className="font-medium">{selectedCustomer.phone}</span></p>
            {selectedCustomer.email && (
              <p>Email: <span className="font-medium">{selectedCustomer.email}</span></p>
            )}
            {selectedCustomer.address && (
              <p className="md:col-span-2">Address: <span className="font-medium">{selectedCustomer.address}</span></p>
            )}
            {(selectedCustomer.city || selectedCustomer.state) && (
              <p>Location: <span className="font-medium">{[selectedCustomer.city, selectedCustomer.state].filter(Boolean).join(', ')}</span></p>
            )}
          </div>
        </div>
      )}

      {/* Search Input */}
      {!selectedCustomer && !showNewCustomerForm && (
        <div className="relative" ref={dropdownRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-gray w-5 h-5" />
            <input
              type="text"
              placeholder="Search customer by name, phone, or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => searchTerm && setShowDropdown(true)}
              className="w-full pl-10 pr-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold focus:border-transparent"
            />
          </div>

          {/* Dropdown */}
          {showDropdown && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-secondary-lightGray rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-secondary-gray">Searching...</div>
              ) : customers.length > 0 ? (
                <div>
                  {customers.map((customer) => (
                    <button
                      key={customer.id}
                      type="button"
                      onClick={() => selectCustomer(customer)}
                      className="w-full px-4 py-3 text-left bg-white hover:bg-secondary-offWhite transition-colors border-b border-secondary-lightGray last:border-b-0 cursor-pointer"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-primary-black">{customer.full_name}</p>
                          <p className="text-sm text-secondary-gray">{customer.phone}</p>
                        </div>
                        <div className="text-right ml-4 flex-shrink-0">
                          <p className="text-xs text-secondary-gray whitespace-nowrap">{customer.customer_code}</p>
                          {customer.email && (
                            <p className="text-xs text-secondary-gray truncate max-w-[150px]">{customer.email}</p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : searchTerm.length >= 2 ? (
                <div className="p-4 text-center text-secondary-gray">No customers found</div>
              ) : (
                <div className="p-4 text-center text-secondary-gray">Type to search customers</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* New Customer Button */}
      {!selectedCustomer && !showNewCustomerForm && (
        <button
          type="button"
          onClick={() => setShowNewCustomerForm(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white border-2 border-dashed border-secondary-lightGray rounded-lg text-secondary-gray hover:border-accent-gold hover:text-accent-gold transition-colors"
        >
          <Plus className="w-5 h-5 flex-shrink-0" />
          <span className="whitespace-nowrap">Add New Customer</span>
        </button>
      )}

      {/* New Customer Form */}
      {showNewCustomerForm && (
        <div className="border border-secondary-lightGray rounded-lg p-4 bg-white">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-primary-black">New Customer Details</h4>
            <button
              type="button"
              onClick={() => setShowNewCustomerForm(false)}
              className="p-1 text-secondary-gray hover:text-red-600 hover:bg-red-50 rounded transition-colors flex items-center justify-center"
              title="Close form"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-primary-black mb-1">
                Full Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={newCustomer.full_name}
                onChange={(e) => handleNewCustomerChange('full_name', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="Enter customer name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-black mb-1">
                Phone <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                value={newCustomer.phone}
                onChange={(e) => handleNewCustomerChange('phone', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="080XXXXXXXX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-black mb-1">
                Email
              </label>
              <input
                type="email"
                value={newCustomer.email}
                onChange={(e) => handleNewCustomerChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="customer@example.com"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-primary-black mb-1">
                Address
              </label>
              <input
                type="text"
                value={newCustomer.address}
                onChange={(e) => handleNewCustomerChange('address', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="Street address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-black mb-1">
                City
              </label>
              <input
                type="text"
                value={newCustomer.city}
                onChange={(e) => handleNewCustomerChange('city', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="e.g., Lagos"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-black mb-1">
                State
              </label>
              <input
                type="text"
                value={newCustomer.state}
                onChange={(e) => handleNewCustomerChange('state', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="e.g., Lagos"
              />
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={createNewCustomer}
              className="flex-1 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-yellow-600 transition-colors"
            >
              <span className="whitespace-nowrap">Add Customer</span>
            </button>
            <button
              type="button"
              onClick={() => setShowNewCustomerForm(false)}
              className="px-4 py-2 bg-white border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-secondary-offWhite transition-colors"
            >
              <span className="whitespace-nowrap">Cancel</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAutocomplete;
